import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { LogoUpim, BotonUpim } from '../components/ui-upim';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { publicAPI } from '../services/api';
import { upcomingPublicEvents } from '../lib/publicEvents';
import {
  ArrowRight,
  Building2,
  Users,
  MapPin,
  Home as HomeIcon,
  Calendar,
  GraduationCap,
  Scale,
  Award,
  CheckCircle2,
  Shield,
  Briefcase,
  Network,
  ChevronRight,
  LayoutDashboard,
  Bot,
  Video,
  BookOpen,
  MessageSquare,
  Cpu,
  Globe
} from 'lucide-react';

const HomePage = () => {
  const stats = [
    { label: 'Propiedades en Renta', value: 'Por confirmar', icon: HomeIcon },
    { label: 'Propiedades en Venta', value: 'Por confirmar', icon: Building2 },
    { label: 'Afiliados Activos', value: 'Por confirmar', icon: Users },
    { label: 'Estados de la República', value: 'Por confirmar', icon: MapPin },
  ];

  const [carousels, setCarousels] = useState([]);
  const [carouselStatus, setCarouselStatus] = useState('loading');
  const aliadosLogos = carousels.filter(item => item.carousel_type === 'aliados');
  const proveedoresLogos = carousels.filter(item => item.carousel_type === 'proveedores');
  useEffect(() => {
    let active = true;
    publicAPI.getCarousels().then(data => {
      if (!Array.isArray(data)) throw new Error('Catalogo invalido');
      if (active) { setCarousels(data.filter(item => item.id && item.name && item.is_active)); setCarouselStatus('ready'); }
    }).catch(() => { if (active) setCarouselStatus('error'); });
    return () => { active = false; };
  }, []);
  const carouselMessage = carouselStatus === 'loading' ? 'Consultando catálogo…'
    : carouselStatus === 'error' ? 'No pudimos cargar el catálogo. Intenta nuevamente más tarde.'
    : 'No hay publicaciones disponibles en este catálogo.';

  const [events, setEvents] = useState([]);
  const [eventStatus, setEventStatus] = useState('loading');
  useEffect(() => {
    let active = true;
    publicAPI.getEvents(true).then(data => {
      const next = upcomingPublicEvents(data);
      if (active) { setEvents(next); setEventStatus('ready'); }
    }).catch(() => { if (active) setEventStatus('error'); });
    return () => { active = false; };
  }, []);

  // Beneficios principales con links a secciones detalladas
  const mainBenefits = [
    {
      id: 'formacion',
      icon: GraduationCap,
      title: 'Formación Continua',
      description: 'Master Classes quincenales, certificación CONOCER EC0110.02 y acceso a la Academia UPIM.',
    },
    {
      id: 'asesoria',
      icon: Scale,
      title: 'Asesoría Integral',
      description: 'Asesoría jurídica, financiera y contable sin costo adicional para todos los afiliados.',
    },
    {
      id: 'networking',
      icon: Network,
      title: 'Red de Networking',
      description: 'Grupos de WhatsApp por zona geográfica y conexión con profesionales inmobiliarios.',
    },
    {
      id: 'blindaje',
      icon: Shield,
      title: 'Blindaje Jurídico',
      description: 'Contratos modelo NOM-247, protocolos y documentos para operaciones seguras.',
    },
    {
      id: 'descuentos',
      icon: Award,
      title: 'Descuentos Exclusivos',
      description: 'Precios preferenciales en avalúos, notarías, seguros y proveedores certificados.',
    },
    {
      id: 'herramientas',
      icon: Briefcase,
      title: 'Herramientas Profesionales',
      description: 'Biblioteca digital con templates, contratos, formatos y material de capacitación.',
    },
  ];

  const upcomingEvents = events.slice(0, 2);

  // Carrusel infinito de logos
  const LogoCarousel = ({ logos, direction = 'left' }) => {
    return (
      <div className="overflow-hidden">
        <div className={`flex space-x-12 ${direction === 'left' ? 'animate-scroll-left' : 'animate-scroll-right'}`}>
          {[...logos, ...logos, ...logos].map((logo, index) => (
            <div 
              key={`${logo.id}-${index}`} 
              className="flex-shrink-0 h-16 w-32 bg-white rounded-lg shadow-sm flex items-center justify-center p-3 grayscale hover:grayscale-0 transition-all"
            >
              <span className="text-xs text-gray-600 font-medium text-center">{logo.name}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="upim-halo relative flex min-h-[80vh] items-center overflow-hidden bg-black" data-testid="home-hero">
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-16">
            {/* Content */}
            <div className="upim-aparece lg:col-span-6">
              <div className="mb-8">
                <LogoUpim className="h-24 lg:h-28" claseTexto="text-5xl" testId="home-logo" />
              </div>

              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.26em] text-[var(--upim-acento)]">
                Desde 2016 · Presencia nacional
              </p>

              <h1 className="mb-6 font-display text-4xl font-extrabold leading-[1.05] text-[var(--upim-texto)] sm:text-5xl lg:text-6xl">
                Profesionaliza tu carrera{' '}
                <span className="text-[var(--upim-texto-3)]">inmobiliaria</span>
              </h1>
              
              <p className="mb-10 max-w-xl text-base leading-relaxed text-[var(--upim-texto-2)]">
                Únete a la asociación más tecnológica e innovadora de México. 
                Accede a formación, asesoría, networking y herramientas exclusivas 
                para impulsar tu negocio.
              </p>
              
              <div className="flex flex-col gap-4 sm:flex-row">
                <BotonUpim as={Link} to="/afiliacion" variante="solido" testId="home-cta-planes" className="px-8 py-3.5">
                  Conocer Planes
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </BotonUpim>
                <BotonUpim as={Link} to="/beneficios" variante="contorno" testId="home-cta-beneficios" className="px-8 py-3.5">
                  Ver Beneficios
                </BotonUpim>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="relative hidden lg:col-span-6 lg:block">
              <div className="relative overflow-hidden rounded-3xl border border-[var(--upim-borde)]">
                <img
                  src="https://images.unsplash.com/photo-1769736438943-3c57cbd02c22?w=800&h=600&fit=crop"
                  alt="Interior de hogar moderno"
                  className="h-auto w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" aria-hidden="true" />
              </div>
              
              {/* Stats Card */}
              <div className="absolute -bottom-7 -left-7 rounded-2xl border border-[var(--upim-borde-fuerte)] bg-[var(--upim-superficie-2)] p-5" data-testid="home-tarjeta-afiliados">
                <div className="flex items-center gap-4">
                  <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--upim-acento-suave)] text-[var(--upim-acento)]">
                    <Users className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-display text-3xl font-extrabold text-[var(--upim-texto)]">Comunidad UPIM</p>
                    <p className="text-xs uppercase tracking-[0.16em] text-[var(--upim-texto-3)]">Profesionales afiliados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y border-[var(--upim-borde)] bg-[var(--upim-superficie-1)] py-14" data-testid="home-indicadores">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="border-l border-[var(--upim-borde)] pl-5">
                <stat.icon className="mb-4 h-5 w-5 text-[var(--upim-acento)]" aria-hidden="true" />
                <p className="font-display text-lg font-extrabold text-[var(--upim-texto)] lg:text-xl">{stat.value}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[var(--upim-texto-3)]">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section con links */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.26em] text-[var(--upim-acento)]">
              ¿Por qué UPIM?
            </p>
            <h2 className="mb-4 font-display text-3xl font-extrabold text-[var(--upim-texto)] lg:text-4xl">
              Todo lo que necesitas para crecer profesionalmente
            </h2>
            <p className="text-gray-600 text-lg">
              Desde 2016, impulsamos la profesionalización del sector inmobiliario en México 
              con beneficios diseñados para tu éxito.
            </p>
          </div>
          
          {/* Benefits Grid - Con links */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainBenefits.map((benefit, index) => (
              <Link 
                key={index}
                to={`/beneficios/${benefit.id}`}
                data-testid={`home-beneficio-${benefit.id}`}
                className="group block rounded-2xl border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-7 transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1.5 hover:border-[var(--upim-borde-fuerte)] hover:bg-[var(--upim-superficie-3)]"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--upim-acento-suave)] transition-colors duration-300 group-hover:bg-[var(--upim-acento)]">
                  <benefit.icon className="h-5 w-5 text-[var(--upim-acento)] transition-colors duration-300 group-hover:text-black" aria-hidden="true" />
                </div>
                <h3 className="mb-2 font-display text-lg font-bold text-[var(--upim-texto)]">{benefit.title}</h3>
                <p className="mb-4 text-sm leading-relaxed text-[var(--upim-texto-2)]">{benefit.description}</p>
                <span className="inline-flex items-center text-sm font-medium text-[var(--upim-acento)]">
                  Conocer más <ChevronRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-14">
            <BotonUpim as={Link} to="/beneficios" variante="contorno" testId="home-ver-beneficios">
              Ver todos los beneficios
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </BotonUpim>
          </div>
        </div>
      </section>

      {/* Plataforma Intranet + CRM + IA */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Plataforma Exclusiva
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Tu plataforma exclusiva como socio UPIM con CRM e inteligencia artificial
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Como afiliado UPIM accedes a una plataforma completa diseñada para potenciar 
                tu negocio inmobiliario con tecnología de vanguardia.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <LayoutDashboard className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Dashboard Personalizado</h4>
                    <p className="text-sm text-gray-600">Gestiona tu membresía, eventos, documentos y beneficios desde un solo lugar.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">CRM con Funciones IA</h4>
                    <p className="text-sm text-gray-600">Próximamente: gestiona tus contactos, leads y operaciones con asistencia de inteligencia artificial.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 p-4 bg-white rounded-lg border border-gray-100">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <Globe className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Bolsa Inmobiliaria Unificada</h4>
                    <p className="text-sm text-gray-600">Conecta tu inventario con la red de afiliados y accede a propiedades de toda la República.</p>
                  </div>
                </div>
              </div>
              
              <Button asChild className="bg-gray-900 hover:bg-gray-800">
                <Link to="/login">
                  Acceder a mi cuenta
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
            
            <div className="relative">
              <img
                src="/assets/event-poster.png"
                alt="Plataforma UPIM con IA"
                className="rounded-2xl shadow-lg w-full object-contain bg-gray-100"
                style={{ minHeight: '400px', maxHeight: '600px' }}
              />
              <div className="absolute -bottom-6 -right-6 bg-gray-900 text-white rounded-xl p-5 shadow-xl hidden md:block">
                <div className="flex items-center space-x-3">
                  <Cpu className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="font-bold">UPIM IA</p>
                    <p className="text-sm text-gray-400">Altitud Inmobiliaria</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academia UPIM */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img
                src="https://images.unsplash.com/photo-1762176263996-a0713a49ee4d?w=700&h=500&fit=crop"
                alt="Academia UPIM - Sala de capacitación corporativa"
                className="rounded-2xl shadow-lg w-full"
              />
            </div>
            
            <div className="order-1 lg:order-2">
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Academia UPIM
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Capacitación continua con tu mismo usuario y contraseña
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Accede a todas las capacitaciones con las credenciales de tu cuenta UPIM. 
                Sin registros adicionales, sin complicaciones.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Master Classes semanales gratuitas</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Webinars exclusivos de UPIM</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Cursos del Colegio Nacional de Inmobiliarios</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Descuentos especiales en certificaciones</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Biblioteca de grabaciones disponible 24/7</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button asChild className="bg-gray-900 hover:bg-gray-800">
                  <Link to="/academia">
                    <Video className="mr-2 h-4 w-4" />
                    Explorar Academia
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-gray-300">
                  <Link to="/calendario">
                    <Calendar className="mr-2 h-4 w-4" />
                    Ver Calendario
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Agenda
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                Próximos eventos
              </h2>
              <p className="text-gray-600 max-w-xl">
                Participa en nuestras capacitaciones, talleres y eventos de networking.
              </p>
            </div>
            <Button asChild variant="outline" className="mt-4 md:mt-0 border-gray-300">
              <Link to="/calendario">
                Ver calendario completo
                <Calendar className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          
          {eventStatus !== 'ready' || upcomingEvents.length === 0 ? (
            <p role="status" className="rounded-2xl border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-8 text-[var(--upim-texto-2)]" data-testid="home-eventos-estado">
              {eventStatus === 'loading' ? 'Consultando próximos eventos…' : eventStatus === 'error' ? 'No se pudo consultar la agenda. Intenta de nuevo más tarde.' : 'No hay próximos eventos publicados por el momento.'}
            </p>
          ) : null}
          <div className="grid md:grid-cols-2 gap-6">
            {upcomingEvents.map((event) => (
              <Card 
                key={event.id} 
                className="overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="grid md:grid-cols-5">
                  <div className="md:col-span-2 h-48 md:h-full overflow-hidden">
                    <img
                      src={event.image_url || '/assets/event-poster.png'}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="md:col-span-3 p-6 flex flex-col justify-center">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(event.date).toLocaleDateString('es-MX', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                      {event.time && <><span className="mx-2">•</span>{event.time} hrs</>}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>
                    <Button asChild size="sm" className="w-fit bg-gray-900 hover:bg-gray-800">
                      <Link to="/calendario">Más información</Link>
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Alianzas Estratégicas */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Alianzas Estratégicas
            </h2>
            <p className="text-gray-600">Instituciones del catálogo público de UPIM</p>
          </div>
          
          {!aliadosLogos.length && <p role="status" className="text-center text-gray-600">{carouselMessage}</p>}
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee">
              {[...aliadosLogos, ...aliadosLogos].map((logo, index) => (
                <div 
                  key={`aliado-${logo.id}-${index}`} 
                  className="flex-shrink-0 h-20 w-40 bg-gray-50 rounded-lg flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all border border-gray-100"
                >
                  <span className="text-sm text-gray-600 font-medium text-center">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Directorio de Proveedores */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Directorio de Proveedores
            </h2>
            <p className="text-gray-600">Descuentos exclusivos para afiliados UPIM</p>
          </div>
          
          {!proveedoresLogos.length && <p role="status" className="text-center text-gray-600">{carouselMessage}</p>}
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee-reverse">
              {[...proveedoresLogos, ...proveedoresLogos].map((logo, index) => (
                <div 
                  key={`proveedor-${logo.id}-${index}`} 
                  className="flex-shrink-0 h-20 w-40 bg-white rounded-lg flex items-center justify-center p-4 grayscale hover:grayscale-0 transition-all border border-gray-100 shadow-sm"
                >
                  <span className="text-sm text-gray-600 font-medium text-center">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Button asChild variant="outline" className="border-gray-300">
              <Link to="/proveedores">
                Ver todos los proveedores
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="upim-halo relative overflow-hidden border-t border-[var(--upim-borde)] bg-black py-24 lg:py-32" data-testid="home-cta-final">
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 lg:px-12">
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.26em] text-[var(--upim-acento)]">
            Afiliación 2026
          </p>
          <h2 className="mb-6 font-display text-3xl font-extrabold leading-tight text-[var(--upim-texto)] lg:text-5xl">
            Impulsa tu carrera inmobiliaria hoy
          </h2>
          <p className="mb-10 max-w-2xl text-base leading-relaxed text-[var(--upim-texto-2)]">
            Forma parte de la comunidad de profesionales inmobiliarios y conoce
            los beneficios de ser parte de UPIM.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <BotonUpim as={Link} to="/afiliacion" variante="solido" testId="home-cta-afiliate" className="px-8 py-3.5">
              Afíliate por $3,500/año
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </BotonUpim>
            <BotonUpim as={Link} to="/contacto" variante="contorno" testId="home-cta-contacto" className="px-8 py-3.5">
              Contactar a UPIM
            </BotonUpim>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
